import React, { useEffect, useState } from 'react';
import { Container, Grid, Card, CardActionArea, CardMedia, CardContent, Typography, Box, CircularProgress, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/api';
import { COLORS, withOpacity } from '../theme/colors';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await productService.getCategories();
        const cats = res.data?.categories || res.data || [];
        setCategories(cats.filter(c => c.isActive !== false));
      } catch (e) {
        console.error('Failed to load categories', e);
        setError('Failed to load categories');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={8}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: COLORS.background.light, minHeight: '100vh', pb: 8 }}>
      <Box sx={{ backgroundColor: COLORS.background.paper, py: 4, mb: 4, borderBottom: `1px solid ${COLORS.border.light}` }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight={700} color={COLORS.text.primary}>Categories</Typography>
          <Typography variant="body1" mt={1} color={COLORS.text.secondary}>Browse all categories and jump directly into products you care about.</Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {categories.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', backgroundColor: COLORS.background.paper, border: `1px solid ${COLORS.border.light}` }}>
            <Typography variant="h6" color={COLORS.text.secondary}>No categories available</Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {categories.map(cat => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={cat.id}>
                <Card elevation={0} sx={{ border: `1px solid ${COLORS.border.light}`, borderRadius: 3, transition: 'all .3s', '&:hover': { boxShadow: `0 8px 24px ${withOpacity(COLORS.secondary.main,0.18)}`, transform: 'translateY(-4px)' } }}>
                  <CardActionArea onClick={() => navigate(`/products?category=${cat.id}`)} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <Box sx={{ position: 'relative', pt: '60%', backgroundColor: COLORS.background.dark }}>
                      {cat.imageUrl ? (
                        <CardMedia component="img" image={cat.imageUrl} alt={cat.name} sx={{ position: 'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover' }} />
                      ) : (
                        <Box sx={{ position: 'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', color: COLORS.text.secondary, fontSize: 18 }}>No Image</Box>
                      )}
                    </Box>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="h6" fontWeight={600} color={COLORS.text.primary} gutterBottom sx={{ fontSize:'1rem' }}>{cat.name}</Typography>
                      {cat.description && <Typography variant="body2" color={COLORS.text.secondary} sx={{ lineHeight:1.4 }}>{cat.description.slice(0,80)}{cat.description.length>80?'...':''}</Typography>}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default CategoriesPage;
